/**
 * Tab body: "Per-pub Reports" — 4 summary cards + per-publisher table
 * for the last 30 days, sourced from /reports/summary. The shave
 * engine recipe is footnoted under the table for ops sanity.
 */
export default function ReportsTab({ summary }) {
  if (!summary) return null;
  return (
    <>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="border border-slate-200 rounded-md p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Our clicks (30d)</p>
          <p className="text-2xl font-mono font-bold text-slate-900 mt-1">{summary.totals?.our_clicks || 0}</p>
          <p className="text-[10px] text-slate-400 mt-1">from sn_clicks</p>
        </div>
        <div className="border border-slate-200 rounded-md p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Feed clicks</p>
          <p className="text-2xl font-mono font-bold text-slate-900 mt-1">{summary.totals?.feed_clicks || 0}</p>
          <p className="text-[10px] text-slate-400 mt-1">from Shopnomix</p>
        </div>
        <div className="border border-slate-200 rounded-md p-4 bg-emerald-50/30">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700">Feed revenue</p>
          <p className="text-2xl font-mono font-bold text-emerald-700 mt-1">${(summary.totals?.feed_revenue || 0).toFixed(2)}</p>
        </div>
        <div className="border border-slate-200 rounded-md p-4 bg-blue-50/30">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-blue-700">Pub paid · margin</p>
          <p className="text-2xl font-mono font-bold text-slate-900 mt-1">
            ${(summary.totals?.pub_earnings || 0).toFixed(2)}
          </p>
          <p className={`text-xs font-mono mt-1 ${
            (summary.totals?.margin || 0) >= 0 ? "text-emerald-700" : "text-rose-600"
          }`}>
            margin ${(summary.totals?.margin || 0).toFixed(2)}
          </p>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
            Per publisher · {summary.start_date} → {summary.end_date}
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Campaign</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Publisher</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-emerald-700 font-bold bg-emerald-50/40">Feed EPC</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Our clicks</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Feed clicks</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-emerald-700 font-bold bg-emerald-50/40">Feed rev</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-blue-700 font-bold bg-blue-50/40">Pub EPC</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-blue-700 font-bold bg-blue-50/40">Pub paid</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-blue-700 font-bold bg-blue-50/40">Margin</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Status</th>
            </tr>
          </thead>
          <tbody>
            {summary.data?.map((row, idx) => (
              <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 text-xs text-slate-700">{row.campaign_label || "—"}</td>
                <td className="px-3 py-2 text-slate-900 text-xs">
                  {row.publisher_email || "(unknown)"}
                  <div className="font-mono text-[10px] text-slate-500">{row.publisher_sub_id}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs bg-emerald-50/30 text-emerald-800">${row.feed_epc.toFixed(4)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{row.our_clicks}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{row.feed_clicks}</td>
                <td className="px-3 py-2 text-right font-mono text-xs bg-emerald-50/30 text-emerald-800">${row.feed_revenue.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs bg-blue-50/30">${row.pub_epc.toFixed(4)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs bg-blue-50/30">${row.pub_earnings.toFixed(2)}</td>
                <td className={`px-3 py-2 text-right font-mono text-xs bg-blue-50/30 ${
                  row.margin >= 0 ? "text-emerald-700" : "text-rose-600"
                }`}>${row.margin.toFixed(2)}</td>
                <td className="px-3 py-2 text-center">
                  {row.pending ? (
                    <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium">pending</span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-medium">synced</span>
                  )}
                </td>
              </tr>
            ))}
            {(!summary.data || summary.data.length === 0) && (
              <tr><td colSpan={10} className="px-4 py-6 text-center text-sm text-slate-400">No traffic in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-4 text-[11px] text-slate-500">
        <div>
          <span className="font-bold text-slate-700">Shave engine:</span>{" "}
          feed ≤ $0.10 → pass-through · $0.10–$0.27 → shave (1¢ default, slab-floored) · ≥ $0.27 → cap at $0.27 (we keep upside).
        </div>
      </div>
    </>
  );
}
